import { Form, FormInstance } from 'antd';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import {
  TaxCoupon,
  TaxCouponDetailRecipientFormValues,
  TaxCouponGeneralForm,
} from '@/core/domain/coupon/coupons/coupons.types';
import { useRouteMatchParam } from '@/hooks';
import { useTaxCouponDetail } from '@/core/domain/coupon/coupons/coupons.hooks';
import moment from 'moment';

interface FormsInstance {
  general?: FormInstance<TaxCouponGeneralForm>;
  recipient?: FormInstance<TaxCouponDetailRecipientFormValues>;
}

interface Forms {
  general?: TaxCouponGeneralForm;
  recipient?: TaxCouponDetailRecipientFormValues;
}

interface InvoiceCFEFormContext {
  forms: Forms;
  formsInstance: FormsInstance;
  taxCoupon?: TaxCoupon & { companyId: number };
}

const InvoiceCFEFormContext = createContext({} as InvoiceCFEFormContext);

export const useInvoiceCFEFormContext = () => useContext(InvoiceCFEFormContext);

const InvoiceCFEFormProvider: React.FC<PropsWithChildren<unknown>> = (
  props,
) => {
  const [forms, setForms] = useState<Forms>({});
  const [formsInstance, setFormsInstance] = useState<FormsInstance>({});

  const companyId = +useRouteMatchParam('companyId');
  const taxCouponId = useRouteMatchParam('invoiceId');

  const taxCouponDetail = useTaxCouponDetail({ taxCouponId });

  useEffect(() => {
    if (taxCouponDetail.data) {
      const { data } = taxCouponDetail;
      setForms((prev) => ({
        ...prev,
        general: {
          createdAt: moment(data.createdAt),
          companyId: companyId,
          id: data.id,
          status: data.status,
          customer: data.recipient,
        },
        recipient: {
          id: data.recipient?.id,
          recipientName: data.recipient?.clientName,
          cpfCnpj: data.recipient?.cpfCnpj,
          hasAddress: !!data.recipient?.deliveryAddress?.zipcode,
          deliveryAddress: data.recipient?.deliveryAddress,
        },
      }));
    }
  }, [taxCouponDetail.dataUpdatedAt]);

  function onFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setFormsInstance((state) => ({ ...state, [name]: info.forms[name] }));
      setForms((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  return (
    <InvoiceCFEFormContext.Provider
      value={{
        forms,
        formsInstance,
        taxCoupon: Object.assign({}, taxCouponDetail.data, { companyId }),
      }}
    >
      <Form.Provider onFormChange={onFormChange}>
        {props.children}
      </Form.Provider>
    </InvoiceCFEFormContext.Provider>
  );
};

export default InvoiceCFEFormProvider;
